import {
    ISlideRenderStateManager, IStatePointEnum, SLIDE_RENDER_ENTRY,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import {
    statusReport007, valueReport007,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import {
    Reporter007, Reporter007ActionType,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType, TDWModuleType, TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { MARK_POINT } from '@src/slide/infrastructure/module/dependent/performance/js-run-point';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

// 同步JS性能相关数据
export interface ISyncJsInfo {
    syncJsCount: number, // 同步js的数量
    syncJsCacheHitCount: number, // 同步js命中缓存的数量
    syncJsAllSize: number, // 同步js总体积
    syncJsFirstReqTime: number, // 最早的同步js开始请求的时间
    syncJsLastReqTime: number, // 最晚的同步js开始请求的时间
    syncJsFirstResTime: number, // 最早的同步js开始响应的时间
    syncJsLastResTime: number, // 最晚的同步js开始响应的时间
    syncJsFirstOverTime: number, // 最早的同步js响应完成的时间
    syncJsLastOverTime: number, // 最晚的同步js响应完成的时间
}

const DELAY_REPORT_TIME = 3000;

export class TtiReport {
    private slideRenderStateManager: ISlideRenderStateManager;
    private presentationConvertStartTime: number;
    // 首次点击屏幕时间
    private firstClickScreenTime = 0;

    public constructor(slideRenderStateManager: ISlideRenderStateManager) {
        this.slideRenderStateManager = slideRenderStateManager;
        this.observeFirstSlideComplete();
        this.observeVisibleSlideComplete();
        this.observePresentationConvertStart();
        // 加入点击监听，判断在首屏列表加载完之前，用户是否会点击页面
        this.addClickListener();
    }

    /**
     * 监听首屏
     */
    private async observeFirstSlideComplete() {
        const { time, entry } = await this.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.firstSlideRender].getPointCompletePromise();
        this.reportTti(time, entry);
    }

    /**
     * 监听屏幕内缩略图
     */
    private async observeVisibleSlideComplete() {
        const { time: tti } = await this.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.firstSlideRender].getPointCompletePromise();
        const { time: visibleTime, entry } = await this.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise();
        const { time: siteCompleteTime } = await this.slideRenderStateManager
            .getStatePoints()[IStatePointEnum.allThumbnailSite].getPointCompletePromise();
        // 3s后再上报列表数据（为了监听在列表显示出来后才点击的占比）
        setTimeout(() => {
            this.reportListLoad(tti, visibleTime, siteCompleteTime, entry);
        }, DELAY_REPORT_TIME);
    }

    /**
     * 接收presentation开始转换时间上报
     * @private
     */
    private observePresentationConvertStart() {
        eventBus.once(EventKeys.PRESENTATION_CONVERT_START, () => {
            this.presentationConvertStartTime = Math.round(performance.now());
        });
    }

    private clickCallback = () =>  {
        this.firstClickScreenTime = Math.round(performance.now());
        this.removeClickListener();
    };

    private addClickListener(): void {
        document.addEventListener('click', this.clickCallback, { capture: true });
    }

    private removeClickListener(): void {
        document.removeEventListener('click', this.clickCallback, { capture: true });
    }

    private getTtiAction() {
        // TDW上报的Action类别
        const TDW_ACTION_TYPE = {
            FIRST_SLIDE: 'FirstSlide', // 常规
            SPECIAL_FIRST_SLIDE: 'SpecialFirstSlide', // 特殊场景【退后台、globals上的会议的toB】
        };
        // 特殊场景过滤
        let tdwAction = TDW_ACTION_TYPE.FIRST_SLIDE;
        if (
            document.hidden // 切换到后台属于特殊场景
            || (global.clientVars?.appInfoConfig && browser.isWindows) // windows上的会议的toB场景浏览器版本比较底
        ) {
            tdwAction = TDW_ACTION_TYPE.SPECIAL_FIRST_SLIDE;
        }
        return tdwAction;
    }

    /**
     * 首页露出后上报各种数据
     * @private
     */
    private async reportTti(tti: number, entry: SLIDE_RENDER_ENTRY) {
        const startTime = global.performance.timing.fetchStart;

        // 只统计 普通移动端和PC端
        const hitEntries = [
            SLIDE_RENDER_ENTRY.EDIT_PC,
            SLIDE_RENDER_ENTRY.THUMBNAIL_MOBILE,
            SLIDE_RENDER_ENTRY.EDIT_MOBILE,
        ];
        if (!hitEntries.includes(entry)) {
            return;
        }

        const data = {
            // js相关信息
            ...this.getSyncJsInfo(),
            // 首页渲染完成距页面开始的耗时
            TTI: tti as number,
            FirstSlide: global.previewTime ? (global.previewTime - startTime) : tti, // fcp
            presentationConvertStart: this.presentationConvertStartTime, // presentation开始转换的时间
            canEdit: global.clientVars?.privilegeAttribute?.can_edit, // 是否拥有编辑权限
            isSSRHit: global.previewTime ? 1 : 0, // 是否命中ssr
            firstSlideElementInfo: await this.getFirstSlideElement(), // 首页元素信息
            sameVersionHit: this.isSameVersionHit(), // 是否访问过当前版本的代码
            pageSize: this.slideRenderStateManager.getPageSize(), // 文档页数
            ttfb: global.performance.timing.responseStart - startTime, // 文档首字节返回距页面开始的耗时
            htmlLoadTime: global.performance.timing.responseEnd - startTime, // 文档下载完距页面开始的耗时
            dataGetBegin: global.clientVars?.__tdw?.beginTime - startTime, // 接口请求开始距页面开始耗时
            dataGetCost: this.getDataGetTime(), // 接口请求耗时
            dataReadyInFirstTask: this.dataReadyInFirstTask(), // 接口返回是在第一个同步task执行完之前的
            mainJsCost: this.getMainJsCost(), // 同步js总耗时
            isHidden: global.document.hidden, // 文档是否处于不可见状态
            isToB: this.isToB(),
            isBig: this.isBig(),
            mainJsInfo: this.mainJsInfo(),
            entry,
            timingDuration: {
                fetchStartToNavigationStart: global.performance.timing.fetchStart - startTime,
                unloadEventStartToNavigationStart: global.performance.timing.unloadEventStart > 0
                    ? global.performance.timing.unloadEventStart - startTime : 0,
                redirectStartToNavigationStart: global.performance.timing.redirectStart > 0
                    ? global.performance.timing.redirectStart - startTime : 0,
                unloadEventEndToNavigationStart: global.performance.timing.unloadEventEnd > 0
                    ? global.performance.timing.unloadEventEnd - startTime : 0,
                redirectEndToNavigationStart: global.performance.timing.redirectEnd > 0
                    ? global.performance.timing.redirectEnd - startTime : 0,
            },
        };
        // tdw上报
        tdw({
            opername: TDWOperationType.DOC_PERFORMANCE,
            module: TDWModuleType.PERFORMANCE_START,
            action: this.getTtiAction(),
            ver5: 0,
            ver6: JSON.stringify(data),
        });
        // 007上报
        valueReport007(Reporter007ActionType.TTI, tti);
        statusReport007(Reporter007ActionType.TTI_SUCCEED_IN_5S, tti <= 5000 ? Reporter007.SUCCESS : Reporter007.ERROR, '', [entry]);
        statusReport007(Reporter007ActionType.WHITE_SCREEN, Reporter007.ERROR);
        window.log?.info('[tti]', data);
    }

    /**
     * 列表缩略图上报各种数据
     * @private
     */
    private reportListLoad(
        tti: number,
        visibleSlideCompleteTime: number,
        siteCompleteTime: number,
        entry: SLIDE_RENDER_ENTRY,
    ) {
        this.removeClickListener();
        const listCellFirstLoadTimeArr = this.slideRenderStateManager.getListCellFirstLoadTimeArr();
        const data = {
            TTI: tti, // 首页渲染完成距页面开始的耗时
            listCellLoadTime: listCellFirstLoadTimeArr, // 列表各缩略图加载完成耗时
            listFirstCellAppearTime: listCellFirstLoadTimeArr[0], // 首个缩略图加载完成耗时
            listInScreenCellAppearTime: visibleSlideCompleteTime, // 首屏全部缩略图加载完成时间
            listSiteCompleteTime: siteCompleteTime, // 全部缩略图占位完成时间
            firstScreenPageSize: this.slideRenderStateManager.getVisibleSlideKeyList().length, // 首屏包含的页数
            canEdit: global.clientVars?.privilegeAttribute?.can_edit, // 是否拥有编辑权限
            pageSize: this.slideRenderStateManager.getPageSize(), // 文档页数
            isHidden: global.document.hidden, // 文档是否处于不可见状态
            mainJsInfo: this.mainJsInfo(),
            firstClickScreenTime: this.firstClickScreenTime, // 用以判断在首屏列表加载完之前，用户是否会点击页面
            entry,
        };
        // 固定统计前3页的最大耗时，便于对比
        const compareNum = 3;
        if (
            listCellFirstLoadTimeArr.length >= compareNum
            && listCellFirstLoadTimeArr.slice(0, compareNum).every(e => !!e)
        ) {
            for (let i = 1; i <= compareNum; i++) {
                data[`pageEnd-${i}`] = Math.max(...listCellFirstLoadTimeArr.slice(0, i));
            }
        }
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PERFORMANCE,
            action: TDWActionType.LIST_LOAD,
            ver6: JSON.stringify(data),
        });
        statusReport007(Reporter007ActionType.LIST_SUCCEED_IN_5S, visibleSlideCompleteTime <= 5000 ? Reporter007.SUCCESS : Reporter007.ERROR, '', [entry]);
        statusReport007(Reporter007ActionType.LIST_SUCCEED_IN_10S, visibleSlideCompleteTime <= 10000 ? Reporter007.SUCCESS : Reporter007.ERROR, '', [entry]);
        window.log?.info('[tti-list]', data);
    }

    /**
     * 是否访问过当前版本的代码（配合是否命中缓存来看缓存效果）
     * @private
     */
    private isSameVersionHit(): boolean {
        const lastVersion = userLocalStorage.getItem(LocalStorageKeys.VERSION_HIT);
        const thisVersion = global.__DWS_INFO__?.releaseVersion;
        if (thisVersion && (lastVersion === thisVersion)) {
            // 当前是部署环境 && 上一次的版本和这一次的版本一样
            return true;
        }
        userLocalStorage.setItem(LocalStorageKeys.VERSION_HIT, thisVersion);
        return false;
    }

    /**
     * 获取js相关数据
     * @private
     */
    private getSyncJsInfo(): ISyncJsInfo {
        // 获取所有同步js信息
        const entries = global.performance.getEntriesByType('resource');
        const syncJs: PerformanceResourceTiming[] = entries
            .filter(e => e?.entryType === 'resource' && /-common~|-base~|-slide-edit/.test(e?.name)) as PerformanceResourceTiming[];
        const syncJsFirstReqTime = Math.round(Math.min(...syncJs.map(e => e.connectStart)));
        const syncJsLastReqTime = Math.round(Math.max(...syncJs.map(e => e.connectStart)));
        const syncJsFirstResTime = Math.round(Math.min(...syncJs.map(e => e.responseStart)));
        const syncJsLastResTime = Math.round(Math.max(...syncJs.map(e => e.responseStart)));
        const syncJsFirstOverTime = Math.round(Math.min(...syncJs.map(e => e.responseEnd)));
        const syncJsLastOverTime = Math.round(Math.max(...syncJs.map(e => e.responseEnd)));
        const syncJsAllSize = Math.round(syncJs.map(e => e.encodedBodySize).reduce((a, b) => a + b, 0) / 1024);
        const syncJsCacheHitCount = syncJs.filter(e => e.responseEnd === e.responseStart).length;
        return {
            syncJsCount: syncJs.length, // 同步js的数量
            syncJsCacheHitCount, // 同步js命中缓存的数量
            syncJsAllSize, // 同步js总体积
            syncJsFirstReqTime, // 最早的同步js开始请求的时间
            syncJsLastReqTime, // 最晚的同步js开始请求的时间
            syncJsFirstResTime, // 最早的同步js开始响应的时间
            syncJsLastResTime, // 最晚的同步js开始响应的时间
            syncJsFirstOverTime, // 最早的同步js响应完成的时间
            syncJsLastOverTime, // 最晚的同步js响应完成的时间
        };
    }

    /**
     * 获取接口请求耗时
     * @private
     */
    private getDataGetTime(): number | undefined {
        const endTime = global.clientVars?.__tdw?.endTime;
        const beginTime = global.clientVars?.__tdw?.beginTime;
        if (endTime && beginTime) {
            return endTime - beginTime;
        }
    }

    /**
     * 接口返回是在第一个同步task执行完之前的
     * @private
     */
    private dataReadyInFirstTask(): boolean | undefined {
        const dataGetEndTime = global.clientVars?.__tdw?.endTime;
        const mainEnd = global.jsRunMarkPoints?.[MARK_POINT.MAIN_END];
        if (dataGetEndTime && mainEnd) {
            return dataGetEndTime < mainEnd;
        }
    }

    /**
     * 获取Main.js执行耗时
     * @private
     */
    private getMainJsCost(): number | undefined {
        const mainStart = global.jsRunMarkPoints?.[MARK_POINT.MAIN_START];
        const mainEnd = global.jsRunMarkPoints?.[MARK_POINT.MAIN_END];
        if (mainStart && mainEnd) {
            return mainEnd - mainStart;
        }
    }

    /**
     * 获取首屏元素信息
     * @private
     */
    private async getFirstSlideElement() {
        const { selectionService } = await this.slideRenderStateManager.getServicesPromise();
        let firstSlideElement = {
            totel: 0,
            table: 0,
            tableCell: 0,
            shape: 0,
            picture: 0,
            connector: 0,
        };
        if ((selectionService.slides || []).length > 0) {
            firstSlideElement = selectionService.slides[0].elementNumInfo;
        }
        return firstSlideElement;
    }

    private isToB() {
        return global.clientVars?.appInfoConfig ? 1 : 0;
    }

    private isBig() {
        const pageSize = this.slideRenderStateManager.getPageSize() || 0;
        const jsonSize = (global.clientVars?.collab_client_vars?.initialAttributedText?.text || '').length / 1024 * 2;
        return (pageSize > 1) && (jsonSize / pageSize > 100) ? 1 : 0;
    }

    private mainJsInfo() {
        return {
            start: global.jsRunMarkPoints?.[MARK_POINT.MAIN_START] - global.performance.timing.responseEnd,
            end: global.jsRunMarkPoints?.[MARK_POINT.MAIN_END] - global.performance.timing.responseEnd,
            during: global.jsRunMarkPoints?.[MARK_POINT.MAIN_END] - global.jsRunMarkPoints?.[MARK_POINT.MAIN_START],
        };
    }
}
